/* eslint-disable @typescript-eslint/no-this-alias */
/* eslint-disable @typescript-eslint/no-non-null-assertion */

import * as Sentry from '@sentry/browser';

import { DEBUG_MODE } from '../../spdConfig';

import util from './util';
import { AudioHandler } from './AudioHandler';
import { Services } from './Services';
import { Characteristics } from './Characteristics';
import { ClientToken, MiddlewareClient } from './core/MiddlewareClient';

const DEBUG_MODE_DEV = true;

interface CardiacData {
  status: string;
  valid: boolean;
  quality: number;
  murmurs: {
    alarm: number;
  };
  cardiac_alarm: number;
}

interface PulmonaryData {
  astma_alarm: number;
  confidence: number;
  quality: number;
  valid: boolean;
  traits: {
    breathing_cycle_present: boolean;
  };
}

export interface AlarmData {
  status: string;
  cardiac: CardiacData;
  pulmonary: PulmonaryData;
}

export class Manager {
  protected authApiToken: ClientToken | null = null;
  private static instance: Manager;

  protected audioHandler = new AudioHandler();

  protected device: BluetoothDevice | null = null;

  protected configService: BluetoothRemoteGATTService | null = null;
  protected audioService: BluetoothRemoteGATTService | null = null;
  protected authService: BluetoothRemoteGATTService | null = null;
  protected infoService: BluetoothRemoteGATTService | null = null;
  protected battService: BluetoothRemoteGATTService | null = null;
  protected uiService: BluetoothRemoteGATTService | null = null;
  protected audioCharacteristic: BluetoothRemoteGATTCharacteristic | null =
    null;

  protected callbackForTouch: (() => void) | null = null;

  /// Singleton getter.
  public static getInstance() {
    if (!Manager.instance) {
      Manager.instance = new Manager();
    }

    return Manager.instance;
  }

  protected async gattConnect() {
    try {
      if (this.device) {
        if (!this.device.gatt) {
          console.error(`Can't connect, 'device.gatt' is undefined!`);
          return false;
        }

        await this.device.gatt.connect();
        this.device.addEventListener('gattserverdisconnected', event => {
          DEBUG_MODE && console.log('Device disconnected', event);
          /// Manager.Callbacks.onDisconnected(event);
        });

        const services = await this.device.gatt.getPrimaryServices();

        this.readServices(services);

        await this.setIconWaiting();

        await this.authenticateDevice(await this.getSerial());

        await this.registerDeviceAudioHandlers();

        await this.startUINotifications();

        /// Manager.Callbacks.onConnected();
        return true;
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error gattConnect() Bluetooth:', error);
      return false;
    }
  }

  public async connect(): Promise<boolean> {
    if (this.device?.gatt?.connected) {
      return true;
    }

    try {
      //localStorage.clear();
      const storedDeviceId = localStorage.getItem('StethoDeviceId');
      DEBUG_MODE && console.log('connect - storedDeviceId', storedDeviceId);
      // Aby zapamiętywanie zadziałało trzeba ustawić chrome://flags/#enable-web-bluetooth-new-permissions-backend flag enabled.
      // @see https://webbluetoothcg.github.io/web-bluetooth/#dom-bluetooth-getdevices
      if (storedDeviceId && 'getDevices' in navigator.bluetooth) {
        const devices = await (
          navigator.bluetooth.getDevices as () => Promise<BluetoothDevice[]>
        ).call(navigator.bluetooth);
        this.device =
          devices.find(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (d: any) => d.id === storedDeviceId,
          ) || null;
        DEBUG_MODE_DEV &&
          console.log('Is stored DeviceId', storedDeviceId, this.device);
        if (await this.gattConnect()) {
          return true; /// jeśli się udało to kończę wpp. próbuję wejść w tryb ręcznego wyboru urządzenia
        }
        this.device = null; /// do posprzątania ????
      }

      if (!this.device) {
        this.device = await navigator.bluetooth.requestDevice({
          filters: [{ services: [Services.SERV_UUID_CONFIG] }],
          optionalServices: [
            Services.SERV_UUID_AUDIO,
            Services.SERV_UUID_AUTH,
            Services.SERV_UUID_INFO,
            Services.SERV_UUID_UI,
            Services.SERV_UUID_BATT,
          ],
        });
        localStorage.setItem('StethoDeviceId', this.device.id);
        await this.gattConnect();
      }
      return true;
    } catch (error) {
      Sentry.captureException(error);
      console.error('Błąd podczas łączenia z urządzeniem Bluetooth:', error);
      return false;
    }
  }

  public async disconnect(): Promise<void> {
    this.device?.gatt?.disconnect();
  }

  public async getStatusBattery() {
    const serialCharacteristic = await this.battService!.getCharacteristic(
      Characteristics.CHAR_UUID_LEVEL,
    );
    const battLevelDataView = await serialCharacteristic.readValue();
    const battLevel = battLevelDataView.getUint8(0); // Pobiera pierwszy bajt, który reprezentuje poziom baterii
    DEBUG_MODE && console.info('Battery level:', battLevel + '%');
    return battLevel;
  }

  public async setIconWaiting() {
    const characteristic = await this.uiService!.getCharacteristic(
      Characteristics.CHAR_POINT,
    );
    const bodyPartCommand = new Uint8Array([0x01, 0x04]);
    await characteristic.writeValue(bodyPartCommand);
  }

  public async setIconSuccess() {
    const characteristic = await this.uiService!.getCharacteristic(
      Characteristics.CHAR_POINT,
    );
    let bodyPartCommand = new Uint8Array([0x01, 0x01]); /// main function
    await characteristic.writeValue(bodyPartCommand);

    bodyPartCommand = new Uint8Array([0x02, 0x02]); // success
    await characteristic.writeValue(bodyPartCommand);
  }

  public async setIconPoint(xPosition: number, yPosition: number) {
    const characteristic = await this.uiService!.getCharacteristic(
      Characteristics.CHAR_POINT,
    );
    let bodyPartCommand = new Uint8Array([0x01, 0x02]); /// test function
    await characteristic.writeValue(bodyPartCommand);

    bodyPartCommand = new Uint8Array([0x03, 0x06]); // front/heart
    await characteristic.writeValue(bodyPartCommand);

    bodyPartCommand = new Uint8Array([0x04, 0x02]); // blank, marked
    await characteristic.writeValue(bodyPartCommand);

    const pointPositionCommand = new Uint8Array([0x05, xPosition, yPosition]); // 0x05 - Set point positions
    await characteristic.writeValue(pointPositionCommand);
  }

  public setCallbackForTouch(callback: () => void) {
    this.callbackForTouch = callback;
  }

  // Zakładam, że `device` to połączone urządzenie Bluetooth
  protected touchState: number | null = null;
  protected async startUINotifications() {
    const that = this;
    /// na razie na szybko jeden callback ale powinno być kilka np. obsługa odłożenia stetho jako hint
    const characteristic = await that.uiService!.getCharacteristic(
      Characteristics.CHAR_POINT,
    );

    // Subskrypcja powiadomień
    await characteristic.startNotifications();

    // Obsługa powiadomień
    characteristic.addEventListener('characteristicvaluechanged', event => {
      if (event.target) {
        const value = (event.target as BluetoothRemoteGATTCharacteristic)
          .value!;
        const eventType = value.getUint8(0); // Pierwszy bajt - typ zdarzenia

        if (eventType === 0x01) {
          // 0x01 oznacza wykrycie dotyku
          const touchState = value.getUint8(1); // Drugi bajt - stan dotyku (0/1)

          if (touchState !== that.touchState) {
            ///reaguję tylko przy zmianie
            that.touchState = touchState;
            if (touchState === 1) {
              DEBUG_MODE && console.log('Touch detected!');
              that.callbackForTouch && that.callbackForTouch();
            } else {
              DEBUG_MODE && console.log('Touch released.');
            }
          }
          /*
          const flags = value.getUint8(2); // Trzeci bajt - flaga

          if (flags & 0x01) {
            DEBUG_MODE && console.log('Internal noise too loud.');
          }
          if (flags & 0x02) {
            DEBUG_MODE && console.log('External noise too loud.');
          }
          if (flags & 0x04) {
            DEBUG_MODE && console.log('Uncertainty: Touch Detector counter too low.');
          }
          if (flags & 0x08) {
            DEBUG_MODE && console.log('Too much movement detected.');
          }
            */
        }
      }
    });
  }

  public async startRecoding() {
    DEBUG_MODE && console.log('Starting audio notifications');
    this.audioHandler.restartAudio();
    await this.audioCharacteristic?.startNotifications();
  }

  // eslint-disable-next-line complexity
  protected visitID: string | null = null;
  public stopRecodingAfter(
    point: number,
    length: number,
    callback: (visit: string) => void,
  ) {
    const that = this;
    this.audioHandler.setStopRecording(length, async () => {
      const visit = await that.stopRecoding(point);
      callback(visit);
    });
  }

  protected async stopRecoding(point: number): Promise<string> {
    await this.audioCharacteristic?.stopNotifications();
    const audio = this.audioHandler.getAudioRecording();

    if (!this.authApiToken) {
      this.authApiToken = await MiddlewareClient.getClientToken();
    }
    if (this.authApiToken) {
      if (!this.visitID) {
        this.visitID = await MiddlewareClient.getVisitId(this.authApiToken);
      }
      if (this.visitID) {
        this.visitID &&
          (await MiddlewareClient.postVisitData(
            this.visitID,
            this.authApiToken,
            point,
            await audio,
          ));
      }
    }
    return JSON.stringify({ visit: this.visitID, point: point });
  }

  public static getSrcFrame(visitId: string, point: number) {
    return MiddlewareClient.getSrcFrame(visitId, point);
  }

  public static async getAlarms(visitId: string, point: number) {
    const authApiToken = await MiddlewareClient.getClientToken();
    if (authApiToken) {
      const data = await MiddlewareClient.getVisitCheck(
        authApiToken,
        visitId,
        point,
      );
      return data as AlarmData;
    }
  }

  public async getAlarmsForVisit(point: number) {
    const authApiToken = await MiddlewareClient.getClientToken();
    if (authApiToken && this.visitID) {
      const data = await MiddlewareClient.getVisitCheck(
        authApiToken,
        this.visitID,
        point,
      );
      return data as AlarmData;
    }
  }

  public async getSerial(): Promise<string> {
    if (!this.device?.gatt?.connected) {
      throw new Error(`Can't get serial, not connected!`);
    }

    // read serial
    const serialCharacteristic = await this.infoService!.getCharacteristic(
      Characteristics.CHAR_UUID_SNVER,
    );
    const serialData = await serialCharacteristic.readValue();
    DEBUG_MODE && console.info('Serial data', serialData);

    const deviceInfo = util.bytesToString(serialData);
    DEBUG_MODE && console.info('Device info', deviceInfo);

    const serial = deviceInfo.split(',')[0];

    return serial;
  }

  protected readServices(services: BluetoothRemoteGATTService[]): void {
    services.forEach(service => {
      DEBUG_MODE && console.log('readServices Service', service.uuid);
      switch (service.uuid) {
        case Services.SERV_UUID_CONFIG:
          this.configService = service;
          break;
        case Services.SERV_UUID_AUDIO:
          this.audioService = service;
          break;
        case Services.SERV_UUID_AUTH:
          this.authService = service;
          break;
        case Services.SERV_UUID_INFO:
          this.infoService = service;
          break;
        case Services.SERV_UUID_BATT:
          this.battService = service;
          break;
        case Services.SERV_UUID_UI:
          this.uiService = service;
          break;
      }
    });
  }

  protected async authenticateDevice(serial: string): Promise<void> {
    DEBUG_MODE && console.log('authenticateDevice', serial);

    const authCharacteristic = await this.authService!.getCharacteristic(
      Characteristics.CHAR_UUID_AUTH,
    );

    try {
      const UID = new Uint8Array([0, 0, 0, 0, 0, 0, 0, 0, 0]);
      await authCharacteristic.writeValue(UID);
    } catch (e) {
      DEBUG_MODE && console.log('Device unauthenticated!', e);

      const authSeed = await authCharacteristic.readValue();

      // get challenge from device
      const deviceChallenge = util.bytesToString(authSeed) + serial;

      // get token for StethoMe API from SDK user callback
      this.authApiToken = await MiddlewareClient.getClientToken();
      // send challenge to StethoMe Device Auth API
      const deviceAuth = await MiddlewareClient.getDeviceAuth(
        this.authApiToken,
        deviceChallenge,
      );
      DEBUG_MODE && console.log('Device auth', { deviceAuth });

      // Parse response
      const deviceAuthUint = deviceAuth
        .split('')
        .map((_, i) => deviceAuth.charCodeAt(i));
      const response = new Uint8Array(17);
      response[0] = 1;
      for (let i = 0; i < 16; i++) {
        response[i + 1] = deviceAuthUint[i];
      }
      await authCharacteristic.writeValue(response);
    }
  }

  protected async registerDeviceAudioHandlers() {
    DEBUG_MODE && console.log('registerDeviceAudioHandlers');
    if (this.audioService) {
      this.audioCharacteristic = await this.audioService.getCharacteristic(
        Characteristics.CHAR_UUID_AUDIO,
      );
      this.audioCharacteristic.addEventListener(
        'characteristicvaluechanged',
        async event => {
          await this.audioHandler.onAudio(event);
        },
      );

      // Channel mode zmieniam na mono, żeby mniej danych przesyłać - nie ma wtedy danych z zewnętrznych mikrofonów.
      if (this.configService) {
        const channelModeChar = await this.configService.getCharacteristic(
          Characteristics.CHAR_UUID_CHANNELMODE,
        );
        const channelChangeCommand = new Uint8Array(1);
        channelChangeCommand[0] = 0x00;
        await channelModeChar.writeValue(channelChangeCommand);
      }
    }
  }
}
